/**
 * Workspace resolver — picks the active workspace id for the request.
 *
 * Resolution order:
 *   1. `?workspace=<uuid>` query param if present (admin/debug override)
 *   2. `GET /api/v1/auth/me` (the authenticated user's default workspace)
 *
 * Persisted workspace pointers are never evidence of an active session.
 */

import { api } from "./api";

const STORAGE_KEY = "meridian.activeWorkspaceId";

export interface WorkspaceSummary {
  id: string;
  name: string;
  slug: string;
  created_at: string;
  updated_at: string;
  accent_color?: string | null;
  logo_url?: string | null;
  ai_instructions?: string | null;
  /** Active AI provider for this workspace (e.g. "claude", "openai").
   *  As of 2026-05-13 this is a platform-level decision (Meridian
   *  superadmin), not a per-workspace customer pick — the backend
   *  populates the field from `platform_ai_config` so the frontend
   *  can still display "current model" indicators consistently. */
  default_ai_provider?: string;
  /** Active provider-namespaced model slug (e.g. "sonnet", "opus",
   *  "gpt-4o"). Same platform-sourced rule as `default_ai_provider`. */
  default_ai_model?: string;
  /** Industry slug (finance / healthcare / etc). Null = generic
   *  vocabulary. Editable from Administration. */
  industry?: string | null;
  experience_mode?: "classic" | "briefing";
  experience_override?: "classic" | "briefing" | null;
}

export type WorkspaceExperienceSelection = "default" | "classic" | "briefing";

export interface IndustryOption {
  slug: string;
  label: string;
}

/** Catalogue of industries the workspace admin can pick from. */
export function listIndustries(): Promise<IndustryOption[]> {
  return api<IndustryOption[]>("/api/v1/workspaces/industries");
}

/** Set the workspace industry slug. Pass empty string to clear (return
 *  to generic AI vocabulary). */
export function updateWorkspaceIndustry(
  workspaceId: string,
  industry: string,
): Promise<WorkspaceSummary> {
  return api<WorkspaceSummary>(
    `/api/v1/workspaces/${workspaceId}/industry`,
    { method: "PATCH", body: { industry } },
  );
}

/** Select a presentation shell. `default` clears the workspace override and
 * inherits the environment setting; no data or backend behavior is forked. */
export function updateWorkspaceExperience(
  workspaceId: string,
  mode: WorkspaceExperienceSelection,
): Promise<WorkspaceSummary> {
  return api<WorkspaceSummary>(
    `/api/v1/workspaces/${workspaceId}/experience`,
    { method: "PATCH", body: { mode } },
  );
}


/** Strict-ish UUID v4/anyversion check — used to reject garbage cached
 *  values like "<uuid>" that crept in from earlier dev experiments. */
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function cleanUuid(raw: string | null | undefined): string | null {
  if (!raw) return null;
  // Strip everything that isn't a hex digit or dash. Catches stray <>, "", quotes.
  const trimmed = raw.replace(/[^0-9a-fA-F-]/g, "");
  return UUID_RE.test(trimmed) ? trimmed.toLowerCase() : null;
}

export async function resolveWorkspaceId(): Promise<string> {
  const fromQuery = cleanUuid(
    new URLSearchParams(window.location.search).get("workspace"),
  );
  if (fromQuery) {
    try {
      window.localStorage.setItem(STORAGE_KEY, fromQuery);
    } catch {
      /* private mode — ignore */
    }
    return fromQuery;
  }

  // Authenticated path — `/auth/me` returns the workspace bound to the
  // session cookie. We try this first so that signing in as a different
  // user always yields the right workspace, regardless of stale cache.
  const me = await api<{ workspace: { id: string } }>("/api/v1/auth/me");
  const id = cleanUuid(me.workspace.id);
  if (!id) {
    throw new Error("The server did not return a valid authenticated workspace.");
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* ignore */
  }
  return id;
}

export function listWorkspaces(): Promise<WorkspaceSummary[]> {
  return api<WorkspaceSummary[]>("/api/v1/workspaces");
}

export interface BrandingUpdate {
  accent_color?: string | null;
  logo_url?: string | null;
}

// ---------------------------------------------------------------------------
// Company research — populated by demo-setup, surfaced as a strategy
// card on Home.
// ---------------------------------------------------------------------------
export interface StrategicCallout {
  theme: string;
  implication: string;
}

export interface CompanyResearch {
  company_name: string;
  description: string;
  business_segments: string[];
  strategic_themes: string[];
  recent_developments: string[];
  key_concerns: string[];
  strategic_callouts: StrategicCallout[];
  confidence: number;
  /** Final URL we fetched (post-redirects) when the demo-setup
   *  pipeline grounded research in a website. Null when research
   *  came purely from training data. */
  source_url?: string | null;
}

export interface ResearchResponse {
  research: CompanyResearch | null;
}

export function fetchWorkspaceResearch(
  workspaceId: string,
): Promise<ResearchResponse> {
  return api<ResearchResponse>(
    `/api/v1/workspaces/${workspaceId}/research`,
  );
}

/** Update a workspace's accent colour + logo. Sparse — pass only the
 *  fields you want to change. Empty string clears the value. */
export function updateWorkspaceBranding(
  workspaceId: string,
  payload: BrandingUpdate,
): Promise<WorkspaceSummary> {
  return api<WorkspaceSummary>(
    `/api/v1/workspaces/${workspaceId}/branding`,
    {
      method: "PATCH",
      body: payload,
    },
  );
}

/** Update the workspace-specific AI instructions. Empty string
 *  clears the override (workspace returns to vanilla AI behaviour). */
export function updateWorkspaceAiInstructions(
  workspaceId: string,
  instructions: string,
): Promise<{ ai_instructions: string | null }> {
  return api<{ ai_instructions: string | null }>(
    `/api/v1/workspaces/${workspaceId}/ai-instructions`,
    {
      method: "PATCH",
      body: { instructions },
    },
  );
}

// ---------------------------------------------------------------------------
// Bring Your Own LLM (docs/specs/BYO_LLM.md)
// ---------------------------------------------------------------------------
export interface ByollmProvider {
  slug: string;
  label: string;
  needs_endpoint: boolean;
  needs_api_version: boolean;
  models: string[];
  key_hint?: string;
  endpoint_hint?: string;
  api_version_hint?: string;
  model_hint?: string;
}

/** One credential bundle (a tier). The key is never returned — `key_set`
 *  only says whether one is stored. */
export interface ByollmTier {
  provider?: string | null;
  model?: string | null;
  base_url?: string | null;
  api_version?: string | null;
  reasoning_effort?: string | null;
  key_set: boolean;
}

export type ByollmTierName = "standard" | "cheap" | "deep";

export interface ByollmConfig {
  enabled: boolean;
  /** Whether the platform-level BYO feature flag is on. A saved config
   *  has no effect until this is true. */
  feature_enabled: boolean;
  /** Always-on default bundle. */
  standard: ByollmTier;
  /** Optional override for light questions — inherits `standard` when unset. */
  cheap: ByollmTier;
  /** Optional override for heavy reasoning — inherits `standard` when unset. */
  deep: ByollmTier;
}

/** Sparse per-tier update. Omit a field to leave it unchanged. For
 *  `api_key`: omit = keep, "" = clear, non-empty = replace (encrypted). */
export interface ByollmTierUpdate {
  provider?: string | null;
  model?: string | null;
  base_url?: string | null;
  api_version?: string | null;
  reasoning_effort?: string | null;
  api_key?: string | null;
}

export interface ByollmUpdate {
  enabled?: boolean;
  standard?: ByollmTierUpdate;
  cheap?: ByollmTierUpdate;
  deep?: ByollmTierUpdate;
}

export interface ByollmTestResult {
  ok: boolean;
  detail: string;
}

/** Catalogue of BYO LLM providers + suggested models for the picker. */
export function listByollmProviders(): Promise<ByollmProvider[]> {
  return api<ByollmProvider[]>("/api/v1/workspaces/byollm/providers");
}

/** Read the workspace's BYO LLM config (key never returned). */
export function getWorkspaceByollm(workspaceId: string): Promise<ByollmConfig> {
  return api<ByollmConfig>(`/api/v1/workspaces/${workspaceId}/byollm`);
}

/** Save the workspace's BYO LLM config. Sparse — see ByollmUpdate. */
export function updateWorkspaceByollm(
  workspaceId: string,
  payload: ByollmUpdate,
): Promise<ByollmConfig> {
  return api<ByollmConfig>(`/api/v1/workspaces/${workspaceId}/byollm`, {
    method: "PATCH",
    body: payload,
  });
}

/** Live-probe the saved BYO LLM credentials. */
export function testWorkspaceByollm(
  workspaceId: string,
): Promise<ByollmTestResult> {
  return api<ByollmTestResult>(
    `/api/v1/workspaces/${workspaceId}/byollm/test`,
    { method: "POST" },
  );
}
